/**
 * The group arm of the share model (SE-8359, PV17 — R06 on SE-8246, SE-8355
 * contract): the one group v1 supports, "Everyone at {organization}", read
 * and written through the entity-neutral `eligibleShareGroups` /
 * `listGroupGrants` / `createGroupGrant` / `updateGroupGrantRole` /
 * `deleteGroupGrant` documents with the shared object's `entityType` and id.
 *
 * Composed by {@link useShareGrants}, never rendered on its own: it owns the
 * two owner-only reads (skipped while the surface is closed), the three
 * writes with their cache updates on the `listGroupGrants` read (the
 * mutation result is written back by hand — no `optimisticResponse`, whose
 * incomplete shape would flood the cache with missing-field errors), the
 * per-line pending set and the refusal copy the surface shows verbatim.
 * @module features/scouting/hooks/useShareGroupGrants
 */

import type { ApolloCache } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import {
  ScoutingListGroupGrantsDocument,
  type ScoutingListGroupGrantsQuery,
  type ShareEntityType,
  ShareGroupKey,
  useScoutingCreateGroupGrantMutation,
  useScoutingDeleteGroupGrantMutation,
  useScoutingEligibleShareGroupsQuery,
  useScoutingListGroupGrantsQuery,
  useScoutingUpdateGroupGrantRoleMutation,
} from "@/generated/graphql";
import {
  removeGroupGrant,
  replaceGroupGrant,
  toGroupWireRole,
  toShareRoleFromGroup,
  type ShareGroupAccessRowItem,
  type ShareGroupCandidate,
} from "@/features/scouting/utils/shareGroupGrants";
import {
  isShareGrantRole,
  type ShareAccessState,
  type ShareGrantRole,
  shareAccessStateFor,
} from "@/features/scouting/utils/shareWatchlistGrants";
import { formatError } from "@/utils/formatError";

/** Arguments for {@link useShareGroupGrants}. */
export interface UseShareGroupGrantsArgs {
  /** Whether the consuming surface is open; gates both reads. */
  readonly isOpen: boolean;
  /** `WATCHLIST` or `SCENARIO`. */
  readonly entityType: ShareEntityType;
  /** Id of the shared object. */
  readonly entityId: string;
}

/** What the orchestration composes. */
export interface ShareGroupGrantsArm {
  /**
   * Whether the group grants are known: "loading" until `listGroupGrants`
   * answered once, "error" when it failed with nothing cached, else "ready".
   * A missing read is NOT "no group grant" — the orchestration folds this
   * into the access state it publishes.
   */
  readonly accessState: ShareAccessState;
  /**
   * Groups the owner may still invite (granted ones are filtered out) —
   * empty until the grants read answered, so a group that already holds a
   * grant is never offered again on the strength of an unanswered read.
   */
  readonly eligibleGroups: readonly ShareGroupCandidate[];
  /** WHO HAS ACCESS group lines, with pending state layered on. */
  readonly groupRows: readonly ShareGroupAccessRowItem[];
  /** The one group grant's role and label, for the effective-role caption. */
  readonly groupRole: ShareGrantRole | null;
  readonly groupLabel: string | null;
  /** Sends `createGroupGrant`; resolves the label for the toast. */
  readonly invite: (
    group: ShareGroupCandidate,
    role: ShareGrantRole
  ) => Promise<void>;
  /** Sends `updateGroupGrantRole`; false when nothing was sent. */
  readonly changeRole: (groupGrantId: string, value: string) => Promise<void>;
  /** Sends `deleteGroupGrant`. */
  readonly revoke: (groupGrantId: string) => Promise<void>;
}

/**
 * The backend's refusal, verbatim, for the surface to show — the codes of
 * the SE-8355 contract all carry a human message; anything else falls back
 * to the generic line the caller supplies.
 * @param error - The value the mutation rejected with
 * @param fallback - Copy when the error carries no message
 * @returns The message to render
 */
export const groupGrantFailureMessage = (
  error: unknown,
  fallback: string
): string => formatError(error).message ?? fallback;

/**
 * Owns the group reads and writes for one shared object.
 * @param args - Hook arguments (see {@link UseShareGroupGrantsArgs})
 * @param args.isOpen - Whether the consuming surface is open
 * @param args.entityType - `WATCHLIST` or `SCENARIO`
 * @param args.entityId - Id of the shared object
 * @returns The group arm the orchestration composes.
 */
export const useShareGroupGrants = ({
  isOpen,
  entityType,
  entityId,
}: UseShareGroupGrantsArgs): ShareGroupGrantsArm => {
  const variables = useMemo(
    () => ({ entityType, entityId }),
    [entityType, entityId]
  );
  const { data: eligibleData } = useScoutingEligibleShareGroupsQuery({
    variables,
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });
  const { data: grantsData, error: grantsError } =
    useScoutingListGroupGrantsQuery({
      variables,
      skip: !isOpen,
      fetchPolicy: "cache-and-network",
    });
  const accessState = shareAccessStateFor({
    hasData: Boolean(grantsData),
    hasError: Boolean(grantsError),
  });
  const [createGroupGrant] = useScoutingCreateGroupGrantMutation();
  const [updateGroupGrantRole] = useScoutingUpdateGroupGrantRoleMutation();
  const [deleteGroupGrant] = useScoutingDeleteGroupGrantMutation();

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const setPending = useCallback((groupGrantId: string, pending: boolean) => {
    setPendingIds(previous => {
      if (previous.has(groupGrantId) === pending) return previous;
      return pending
        ? new Set([...previous, groupGrantId])
        : new Set([...previous].filter(id => id !== groupGrantId));
    });
  }, []);

  const grants = useMemo(() => grantsData?.listGroupGrants ?? [], [grantsData]);

  const groupRows = useMemo(
    (): readonly ShareGroupAccessRowItem[] =>
      grants.map(grant => ({
        groupGrantId: grant.id,
        key: grant.group.key,
        label: grant.group.label,
        memberCount: grant.group.memberCount,
        role: toShareRoleFromGroup(grant.role),
        isPending: pendingIds.has(grant.id),
      })),
    [grants, pendingIds]
  );

  /** Granted groups are changed in place, never offered again (R06 §2). */
  const eligibleGroups = useMemo((): readonly ShareGroupCandidate[] => {
    if (accessState !== "ready") return [];
    const grantedKeys = new Set(grants.map(grant => grant.group.key as string));
    return (eligibleData?.eligibleShareGroups ?? [])
      .filter(group => !grantedKeys.has(group.key))
      .map(group => ({
        key: group.key,
        label: group.label,
        memberCount: group.memberCount,
      }));
  }, [accessState, eligibleData, grants]);

  // One group per object in v1: the first grant IS the group.
  const groupRole = groupRows[0]?.role ?? null;
  const groupLabel = groupRows[0]?.label ?? null;

  const writeRead = useCallback(
    (
      cache: ApolloCache<unknown>,
      update: (
        previous: ScoutingListGroupGrantsQuery | null
      ) => ScoutingListGroupGrantsQuery | undefined
    ) => {
      cache.updateQuery<ScoutingListGroupGrantsQuery>(
        { query: ScoutingListGroupGrantsDocument, variables },
        previous => update(previous) ?? null
      );
    },
    [variables]
  );

  const invite = useCallback(
    async (group: ShareGroupCandidate, role: ShareGrantRole) => {
      await createGroupGrant({
        variables: {
          input: {
            entityType,
            entityId,
            // v1 knows one key; a later value arrives through the candidate.
            groupKey: group.key as ShareGroupKey,
            role: toGroupWireRole(role),
          },
        },
        update: (cache, { data }) => {
          const grant = data?.createGroupGrant;
          if (!grant) return;
          // A repeat create updates the grant in place (SE-8355), so the
          // returned row replaces any cached one with the same id.
          writeRead(cache, previous => replaceGroupGrant(previous, grant));
        },
      });
    },
    [createGroupGrant, entityType, entityId, writeRead]
  );

  const changeRole = useCallback(
    async (groupGrantId: string, value: string) => {
      const row = groupRows.find(item => item.groupGrantId === groupGrantId);
      if (!row || !isShareGrantRole(value) || row.role === value) return;
      setPending(groupGrantId, true);
      try {
        await updateGroupGrantRole({
          variables: {
            input: { groupGrantId, role: toGroupWireRole(value) },
          },
          update: (cache, { data }) => {
            const grant = data?.updateGroupGrantRole;
            if (!grant) return;
            writeRead(cache, previous => replaceGroupGrant(previous, grant));
          },
        });
      } finally {
        setPending(groupGrantId, false);
      }
    },
    [groupRows, setPending, updateGroupGrantRole, writeRead]
  );

  const revoke = useCallback(
    async (groupGrantId: string) => {
      setPending(groupGrantId, true);
      try {
        await deleteGroupGrant({
          variables: { input: { groupGrantId } },
          // `{ removed: false }` (already gone, or another organization's id)
          // still drops the line: the grant is not there either way.
          update: cache => {
            writeRead(cache, previous =>
              removeGroupGrant(previous, groupGrantId)
            );
            cache.evict({
              id: cache.identify({
                __typename: "GroupGrant",
                id: groupGrantId,
              }),
            });
            cache.gc();
          },
        });
      } finally {
        setPending(groupGrantId, false);
      }
    },
    [deleteGroupGrant, setPending, writeRead]
  );

  return useMemo(
    (): ShareGroupGrantsArm => ({
      accessState,
      eligibleGroups,
      groupRows,
      groupRole,
      groupLabel,
      invite,
      changeRole,
      revoke,
    }),
    [
      accessState,
      eligibleGroups,
      groupRows,
      groupRole,
      groupLabel,
      invite,
      changeRole,
      revoke,
    ]
  );
};
